/* ==========================================================================
   Sisters Moto: "virtual parent" grouping for riding gear (assets/sm-group.js)

   Every size and colour of a glove / helmet / jersey / pant / boot is its own
   Shopify product (one "Default Title" variant). This file groups them back
   together in the browser, without changing products and without an app:

   - SmGroup.parse(title, vendor) -> { model, modelKey, color, colorKey, design, size,
     sizeKey, sizeRank }   (pure function, also used by the Node tests). Graphic names
     ("Supertech M10 Helmet - Flood - MIPS - Blue", "Thor Sportmode Anarchy Gloves") are part of
     the colourway, so all graphics of one model share a card.
   - Collection pages: siblings come from one collection-wide index,
     /collections/<handle>?view=sm-group-index&page=N (templates/collection.sm-group-index.liquid,
     250 products per page, page 1 first, then 3 at a time). Kept in localStorage for 6 hours
     (refreshed in the background after 30 minutes; sessionStorage / memory when storage is full
     or blocked; older collection indexes are dropped first when the quota is hit).
     Cards render at once from the products on the page, then fill in when the index arrives.
   - Search results page, product page, and fallback when the index fails:
     SmGroup.siblings(model, vendor, modelKey) -> Promise<[member]> from
     templates/search.sm-siblings.liquid (/search?q=<model words>&type=product&view=sm-siblings),
     filtered to the same modelKey, cached per model in sessionStorage for 30 minutes.
     The product page first looks in any collection index already on the device.
   - Collection / search grids (cards rendered with data-sm-gear, see
     snippets/sm-card-core.liquid): later cards of a model already shown on the
     page are removed, the first card gets colour swatches + size buttons, and
     its price / image / links / Add to cart follow the selection. Re-runs after
     the filters re-render #ProductGridContainer. Collection grids with data-smg-limit show
     that many style cards, then a "Show more styles" button.
   - Product page (snippets/sm-group-pdp.liquid): colour + size buttons that
     open the sibling product.
   - Cards of products with REAL Colour / Size variants (any page; snippets/sm-card-core.liquid
     marks them data-sm-variants with the variants as JSON): the same swatches + size buttons,
     one member per variant (section 4b). Price, links (?variant=), SKU, image and Add to cart
     follow the picked variant. assets/quick-add-square.js loads this file for such cards on
     pages whose section did not.

   - Parts / stickers (data-sm-group-mode="color" from snippets/sm-group-gear.liquid, which
     outputs "color" for them): COLOUR-ONLY groups by a strict title rule (parseColor, section 1b),
     never a size row; a group is only used when every member has its own colour and the part
     numbers belong to one family (colourGroupOk), otherwise its cards stay separate. Grids group
     parts ONLY with colour siblings on the same page (no collection index, no sibling searches);
     the product page does one search (one result page) to show every colour. Same price / link /
     image / Add to cart behaviour as gear; the product page shows only the colour row.

   Only cards / pages marked as gear (or colour-mode parts) by Liquid do any work. On search
   pages, sibling requests start only when a card scrolls near the viewport.
   ========================================================================== */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
    return;
  }
  if (root.SmGroup && root.SmGroup.__sm) return; // the tag can be on the page twice
  var api = factory();
  root.SmGroup = api;
  api._boot();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* ------------------------------------------------------------------------
     1. Parser
     ------------------------------------------------------------------------ */

  // Colour vocabulary (lower case). Modifiers like "matte" / "light" count as
  // colour words when they sit in the trailing colour run.
  var COLOR_WORDS = (
    'black white red blue navy grey gray green lime orange yellow fluo fluorescent flo neon hi vis hi-vis hivis hv ' +
    'pink purple violet teal aqua cyan turquoise tan khaki sand brown olive camo kryptek silver gold charcoal stealth ' +
    'matte matt gloss glossy satin carbon dark light bright burgundy maroon magenta coral mint cream bone gunmetal ' +
    'graphite chrome bronze copper titanium slate smoke clear iridium dk lt rust petrol indigo royal cobalt electric ' +
    'wht blk blu grn org ylw gry multi moss sage mauve lilac lavender plum berry wine ivory beige sky ice stone steel ' +
    'ocean forest army desert rose peach fuchsia citron acid volt fire anthracite sunset lemon mustard mocha coffee ' +
    'fade chocolate cherry crimson scarlet emerald jade azure denim midnight pearl platinum iron cement concrete ash ' +
    'heather natural nude raspberry tangerine amber honey dirt mud orchid vintage metallic iridescent reflective ' +
    'obskura skyfall gry/blk white/black'
  ).split(/\s+/);
  var COLOR_SET = {};
  COLOR_WORDS.forEach(function (w) { COLOR_SET[w] = 1; });

  function isColorWord(w) {
    w = String(w || '').toLowerCase().replace(/[^a-z\-]/g, '');
    return !!w && COLOR_SET[w] === 1;
  }

  // A whitespace token that is part of a colour: a colour word, or a slash
  // combination of plain words where at least one part is a colour word
  // ("Pink/Mauve", "Black/Hi", "Vis/Grey").
  function isColorToken(tok) {
    if (isColorWord(tok)) return true;
    if (tok.indexOf('/') < 0) return false;
    var parts = tok.split('/');
    var hasColor = false;
    for (var i = 0; i < parts.length; i++) {
      var p = parts[i];
      if (p === '') continue;
      if (!/^[A-Za-z][A-Za-z.'\-]*$/.test(p)) return false;
      if (isColorWord(p)) hasColor = true;
    }
    return hasColor;
  }

  // ---- sizes ----
  // Letter sizes, normalised to a display label and a rank.
  var LETTER = {
    'xxxs': '3XS', '3xs': '3XS', 'xxs': '2XS', '2xs': '2XS', 'xs': 'XS', 'x-small': 'XS', 'xsmall': 'XS', 'extra small': 'XS',
    's': 'S', 'sm': 'S', 'small': 'S',
    'm': 'M', 'md': 'M', 'med': 'M', 'medium': 'M',
    'l': 'L', 'lg': 'L', 'large': 'L',
    'xl': 'XL', 'x-large': 'XL', 'xlarge': 'XL', 'extra large': 'XL',
    'xxl': '2XL', '2xl': '2XL', '2x': '2XL', 'xx-large': '2XL', 'xxlarge': '2XL', '2x-large': '2XL',
    'xxxl': '3XL', '3xl': '3XL', '3x': '3XL', 'xxx-large': '3XL', '3x-large': '3XL',
    '4xl': '4XL', '4x': '4XL', '4x-large': '4XL', 'xxxxl': '4XL',
    '5xl': '5XL', '5x': '5XL', '5x-large': '5XL',
    'xx-small': '2XS', 'xxs/xs': '2XS/XS', '2xs/xs': '2XS/XS',
    'one size': 'One size', 'os': 'One size', 'osfa': 'One size', 'osfm': 'One size'
  };
  var LETTER_ORDER = ['3XS', '2XS', '2XS/XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];
  var YOUTH = {
    'y3xs': 'Y3XS', 'yxxxs': 'Y3XS', 'y2xs': 'Y2XS', 'yxxs': 'Y2XS', 'yxs': 'YXS', 'ys': 'YS', 'ysm': 'YS', 'ym': 'YM', 'ymd': 'YM', 'yl': 'YL', 'ylg': 'YL', 'yxl': 'YXL',
    'youth xs': 'YXS', 'youth s': 'YS', 'youth m': 'YM', 'youth l': 'YL', 'youth xl': 'YXL',
    'youth x-small': 'YXS', 'youth small': 'YS', 'youth medium': 'YM', 'youth large': 'YL', 'youth x-large': 'YXL'
  };
  var YOUTH_ORDER = ['Y3XS', 'Y2XS', 'YXS', 'YS', 'YM', 'YL', 'YXL'];
  // "Lt" / "Xt" etc. = tall sizes (Fly Racing pants)
  var TALL = { 'st': 'S', 'mt': 'M', 'lt': 'L', 'xt': 'XL', 'xlt': 'XL', '2xt': '2XL', '2xlt': '2XL', '3xt': '3XL' };

  var XS_ = '[\\s\\-]?';
  var L_ALT = 'xxxs|3xs|xxs\\/xs|2xs\\/xs|xxs|2xs|xx' + XS_ + 'small|x' + XS_ + 'small|extra small|xs|sm|small|med|medium|md|large|lg|' +
    'xxx' + XS_ + 'large|3x' + XS_ + 'large|xx' + XS_ + 'large|2x' + XS_ + 'large|4x' + XS_ + 'large|5x' + XS_ + 'large|x' + XS_ + 'large|' +
    'extra large|xxxxl|xxxl|xxl|xl|2xl|3xl|4xl|5xl|2x|3x|4x|5x|s|m|l|one size|osfa|osfm|os';
  var Y_ALT = 'y-(?:xxs|2xs|xs|s|m|l|xl)|y3xs|yxxxs|y2xs|yxxs|yxs|ysm|ys|ymd|ym|ylg|yl|yxl|youth (?:x-small|small|medium|large|x-large|xs|s|m|l|xl)';
  var T_ALT = '2xlt|2xt|3xt|xlt|xt|lt|mt|st';
  var NUM = '\\d{1,2}(?:\\.5)?(?:\\/\\d{1,2})?';
  var ONE = '(?:' + Y_ALT + '|' + L_ALT + ')';
  // A single size, a range ("Xs-Lg", "Md/Lg", "Ys Yl", "Xl 2x") of letter sizes, a tall size,
  // "Sz 32" / "Size 32" / "Sz 32 Tall" / "Size Large", "12 mm" pad thickness.
  var SIZE_CORE =
    '(?:' + ONE + '\\s+\\d{1,2}\\s?mm|\\d{1,2}\\s?mm\\s+' + ONE +
    '|' + ONE + '\\s+youth' +
    '|(?:sz|size)\\.?\\s*' + NUM + '(?:\\s+tall)?' +
    '|size\\s+' + ONE + '(?:\\s+tall)?' +
    '|' + ONE + '(?:\\s*[\\/\\-]\\s*|\\s+)' + ONE +
    '|' + ONE + '(?:\\s+tall)?' +
    '|' + T_ALT +
    '|\\d{1,2}\\s?mm)';
  var RE_SIZE_TAIL = new RegExp('(?:^|[\\s,|\\-])(' + SIZE_CORE + ')\\s*$', 'i');
  var RE_NUM_TAIL = new RegExp('\\s(' + NUM + ')\\s*$');
  // Trailing part number: "3563623-1317-M", "2701-1052", "12-3108", "PCHMGREEN", "05-5698"
  var RE_SKU = /\s+(?=[A-Z0-9.\-]*\d)[A-Z0-9][A-Z0-9.\-]{3,}\s*$/;
  var RE_SKU_LETTERS = /\s+[A-Z]{6,}\s*$/; // all-caps letter codes like PCHMGREEN (only after " - ")

  function clean(s) {
    return String(s || '')
      .replace(/[®™©?]/g, '')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/\s+-(?=\S)/g, ' - ') // "Fluo- Small" / "Fluo -Small"
      .replace(/(\S)-\s+/g, function (m, a) { return /[A-Za-z]/.test(a) ? a + ' - ' : m; })
      .replace(/\s+/g, ' ')
      .trim();
  }

  // size text -> { label, key, rank }
  function normSize(raw) {
    if (!raw) return { label: '', key: '', rank: 9999 };
    var s = String(raw).trim().replace(/\s+/g, ' ');
    var low = s.toLowerCase();
    // "Lg 12mm" / "18mm M": size + pad thickness
    var mm = /^(.+?)\s+(\d{1,2})\s?mm$/.exec(low) || /^(\d{1,2})\s?mm\s+(.+)$/.exec(low);
    if (mm) {
      var sizePart = /mm/.test(mm[0].split(/\s+/)[0]) ? mm[2] : mm[1];
      var mmPart = /mm/.test(mm[0].split(/\s+/)[0]) ? mm[1] : mm[2];
      var inner = normSize(sizePart);
      var lab = inner.label + ' ' + parseInt(mmPart, 10) + 'mm';
      return { label: lab, key: lab.toLowerCase(), rank: inner.rank + parseInt(mmPart, 10) / 1000 };
    }
    low = low.replace(/^(\d?x{0,3})[\s\-]?(large|small)$/, function (m0, a, b) { return a ? a + '-' + b : b; });
    low = low.replace(/^y-(\w+)$/, 'y$1');
    var yo = /^(.+?)\s+youth$/.exec(low);
    if (yo) low = 'youth ' + yo[1];
    var tall = false;
    if (/\stall$/.test(low)) { tall = true; low = low.replace(/\s+tall$/, ''); }
    if (TALL[low]) { tall = true; low = TALL[low].toLowerCase(); }
    low = low.replace(/^size\s+/, '');
    var m = /^(?:sz|size)\.?\s*(.+)$/.exec(low);
    if (m) low = m[1];
    var label, rank;
    if (/^\d{1,2}\s?mm$/.test(low)) {
      label = low.replace(/\s/g, '') ; rank = 3000 + parseFloat(low);
    } else if (/^\d{1,2}(?:\.5)?(?:\/\d{1,2})?$/.test(low)) {
      var n = low.split('/')[0];
      label = low.replace(/^0(\d)/, '$1').replace(/\/0(\d)/, '/$1');
      rank = 2000 + parseFloat(n);
    } else if (YOUTH[low]) {
      label = YOUTH[low]; rank = 100 + YOUTH_ORDER.indexOf(label);
    } else if (LETTER[low]) {
      label = LETTER[low];
      var i = LETTER_ORDER.indexOf(label);
      rank = label === 'One size' ? 5000 : 1000 + (i < 0 ? 50 : i * 2);
    } else {
      // ranges: "Xs-Lg", "Md/Lg", "Ys Yl", "Xl 2x"
      var parts = low.split(/\s*[\/\-]\s*|\s+/).filter(Boolean);
      var labs = parts.map(function (p) { return YOUTH[p] || LETTER[p] || p.toUpperCase(); });
      label = labs.join('/');
      var first = labs[0];
      rank = YOUTH_ORDER.indexOf(first) >= 0 ? 100 + YOUTH_ORDER.indexOf(first) + 0.25
        : (LETTER_ORDER.indexOf(first) >= 0 ? 1000 + LETTER_ORDER.indexOf(first) * 2 + 0.25 : 4000);
    }
    if (tall) { label += ' Tall'; rank += 0.5; }
    return { label: label, key: label.toLowerCase(), rank: rank };
  }

  // Vendor name variants to strip from the start of the model ("Fly Racing", "Fly", "100%" ...)
  function vendorVariants(vendor) {
    var v = String(vendor || '').toLowerCase().replace(/[^a-z0-9% ]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (!v) return [];
    var out = [v];
    var short = v.replace(/\b(racing|helmets?|offroad|off road|moto|mx|usa|inc|co|company|products?)\b/g, '').replace(/\s+/g, ' ').trim();
    if (short && out.indexOf(short) < 0) out.push(short);
    var first = v.split(' ')[0];
    if (first.length >= 2 && out.indexOf(first) < 0) out.push(first);
    if (/^100 ?percent$|^100%$/.test(v)) out.push('100percent', '100 percent', '100%');
    if (/^moose/.test(v)) out.push('moose racing', 'moose offroad', 'moose');
    if (/^fly/.test(v)) out.push('fly racing', 'fly');
    out.sort(function (a, b) { return b.length - a.length; });
    return out;
  }

  var WORD_MAP = {
    yth: 'youth', kids: 'youth', kid: 'youth', hlmt: 'helmet', helmets: 'helmet', gloves: 'glove', glv: 'glove',
    pants: 'pant', jerseys: 'jersey', jsy: 'jersey', boots: 'boot', jackets: 'jacket', jkt: 'jacket',
    shld: 'shield', womens: 'womens', women: 'womens', ladies: 'womens', lady: 'womens', wmns: 'womens',
    mens: '', men: '', w: 'with', mips: 'mips'
  };

  function keyOf(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/100\s*%/g, '100percent')
      .replace(/'s\b/g, 's')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .split(' ')
      .map(function (w) { return Object.prototype.hasOwnProperty.call(WORD_MAP, w) ? WORD_MAP[w] : w; })
      .filter(Boolean)
      .join(' ');
  }

  var COLOR_MAP = {
    gray: 'grey', gry: 'grey', blk: 'black', wht: 'white', blu: 'blue', grn: 'green', org: 'orange', ylw: 'yellow',
    dk: 'dark', lt: 'light', flo: 'fluo', fluorescent: 'fluo', hv: 'hi vis', hivis: 'hi vis', matt: 'matte', glossy: 'gloss'
  };
  function colorKeyOf(c) {
    return String(c || '')
      .toLowerCase()
      .replace(/hi[\s\-]?vis/g, 'hivis')
      .replace(/[^a-z0-9\/]+/g, ' ')
      .split(/\s+/)
      .map(function (w) {
        return w.split('/').map(function (p) { return COLOR_MAP[p] || p; }).join('/');
      })
      .join(' ')
      .replace(/\s*\/\s*/g, '/')
      .trim();
  }

  // Strip the vendor from the start of a display string (keeps original casing).
  function stripVendor(text, vendor) {
    var vs = vendorVariants(vendor);
    var t = text;
    for (var i = 0; i < vs.length; i++) {
      var pat = vs[i].replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, '[\\s\\-]+');
      var re = new RegExp('^' + pat + '(?=[\\s\\-:]|$)[\\s\\-:]*', 'i');
      if (re.test(t)) { t = t.replace(re, ''); break; }
    }
    return t.trim() || text;
  }

  // Segments after " - " that describe the model, not a colour ("MIPS", "MIPS®", "CE", "ECE 22.06")
  var RE_FEATURE_SEG = /^(?:mips|ce|ece(?:\s*22\.?0?6)?|dot|waterproof|wp|vented|ventilated|supervented|d3o|drystar|gore-?tex|gtx|h2o|heated|youth|womens?|women's|mens|men's|tall)$/i;

  // Graphic names inside the model name. Only for brands whose titles read
  // "<line> <graphic> <product>" and only for the lines listed here, so the graphic word is
  // known to sit between them: "Sportmode Anarchy Gloves" -> model "Sportmode Gloves",
  // design "Anarchy". Words that change the product (VARIANT_WORDS, anything with a digit)
  // stay in the model: "Sportmode Vented Iconic Gloves" -> "Sportmode Vented Gloves" + "Iconic".
  var LINES = {
    fly: {
      lines: ['formula s carbon', 'formula carbon', 'formula cc', 'formula cp', 'evolution dst', 'trekker cw', 'pro lite',
        'kinetic', 'revolt', 'rayce', 'trekker', 'sentinel', 'lite', 'f 16'],
      nouns: /^(?:gloves?|jerseys?|pants?|helmets?|hlmt|visor|bib|shorts?)$/i
    },
    thor: {
      lines: ['reflex sport', 'sportmode', 'launchmode', 'ridemode', 'pulse', 'prime', 'sector', 'terrain', 'spectrum',
        'range', 'hallman', 'fleet', 'intense', 'assist'],
      nouns: /^(?:gloves?|jerseys?|pants?|helmets?|shorts?)$/i
    },
    alpinestars: {
      lines: ['racer', 'fluid', 'techstar', 'supertech'],
      nouns: /^(?:jerseys?|pants?)$/i
    }
  };
  var VARIANT_WORDS = {
    mesh: 1, vented: 1, xp: 1, cw: 1, cold: 1, weather: 1, pro: 1, lite: 1, carbon: 1, carbn: 1, flow: 1, heated: 1,
    gauntlet: 1, long: 1, short: 1, sb: 1, digit: 1, gp: 1, mx: 1, air: 1, mtb: 1, bicycle: 1, youth: 1, yth: 1, womens: 1,
    "women's": 1, girls: 1, "girl's": 1, kids: 1, tall: 1, adv: 1, enduro: 1, trail: 1, waterproof: 1, wp: 1, jacket: 1,
    liner: 1, cheek: 1, pad: 1, pads: 1, screw: 1, screws: 1, vent: 1, spoiler: 1, kit: 1, set: 1, strap: 1, peak: 1
  };
  var RE_WHO_PREFIX = /^(?:youth|yth|women'?s|womens|girl'?s|girls|men'?s|kids)$/i;

  function lineGraphic(display, vendor) {
    var v = String(vendor || '').toLowerCase();
    var conf = /^fly\b/.test(v) ? LINES.fly : (/^thor\b/.test(v) ? LINES.thor : (/^alpinestars\b/.test(v) ? LINES.alpinestars : null));
    if (!conf) return null;
    var toks = display.split(/\s+/);
    var i = 0;
    while (i < toks.length && RE_WHO_PREFIX.test(toks[i])) i++;
    var low = toks.map(function (t) { return t.toLowerCase(); });
    var lineLen = 0;
    for (var li = 0; li < conf.lines.length && !lineLen; li++) {
      var lw = conf.lines[li].split(' ');
      if (low.slice(i, i + lw.length).join(' ') === conf.lines[li]) lineLen = lw.length;
    }
    if (!lineLen) return null;
    var start = i + lineLen;
    var n = start;
    while (n < toks.length && !conf.nouns.test(toks[n])) n++;
    if (n >= toks.length) return null;
    var middle = toks.slice(start, n);
    // "Helmet Visor" and "Visor" are the same part ("Formula Cc Driver Helmet Visor" / "Formula Cc Glide Visor")
    var noun = toks.slice(n);
    if (noun.length > 1 && /^(?:helmets?|hlmt)$/i.test(noun[0]) && /^visors?$/i.test(noun[1])) noun = noun.slice(1);
    if (noun.length > 3) return null;
    var keep = [];
    var graphic = [];
    middle.forEach(function (t) {
      var k = t.toLowerCase().replace(/[^a-z0-9']/g, '');
      if (VARIANT_WORDS[k] || /\d/.test(t)) keep.push(t);
      else graphic.push(t);
    });
    var model = toks.slice(0, start).concat(keep, noun).join(' ');
    if (!graphic.length || graphic.length > 3) {
      // no graphic: still use the "Visor" spelling so both spellings group
      return noun.length !== toks.length - n ? { model: toks.slice(0, n).concat(noun).join(' '), design: '' } : null;
    }
    return { model: model, design: graphic.join(' ') };
  }

  function parse(title, vendor) {
    var s = clean(title);
    var size = '';
    var base = s;

    // a) trailing part number, then trailing size (try with and without the part number)
    var noSku = s.replace(RE_SKU, '').replace(/\s+[A-Za-z]\d[a-z]$/, '');
    var cands = noSku !== s ? [noSku, s] : [s];
    var found = false;
    for (var i = 0; i < cands.length && !found; i++) {
      var c = cands[i];
      var m = RE_SIZE_TAIL.exec(c);
      if (m) {
        size = m[1];
        base = c.slice(0, m.index + (m[0].length - m[0].replace(/^[\s,|\-]/, '').length));
        base = base.replace(RE_SKU, '');
        found = true;
        break;
      }
      // bare number ("Tech 7 Boots Black 10", "Pants - Black - 32"): only after a colour word or " - "
      var n = RE_NUM_TAIL.exec(c);
      if (n) {
        var before = c.slice(0, n.index).trim();
        var lastTok = before.split(' ').pop();
        if (/\s-$/.test(c.slice(0, n.index + 1)) || / -$/.test(before) || isColorToken(lastTok)) {
          size = n[1];
          base = before;
          found = true;
        }
      }
    }
    if (!found) base = noSku;
    base = base.replace(/[\s,|\-]+$/, '').replace(/\s+/g, ' ').trim();
    // all-caps letter code after a dash segment ("Mohawk - Green PCHMGREEN")
    if (/ - /.test(base)) base = base.replace(RE_SKU_LETTERS, '');

    // "... Glove Black WOMENS": a trailing gender / age word belongs to the model
    var who = '';
    var wm = /\s+(women'?s|womens|ladies|mens|men's|youth|kids)$/i.exec(base);
    if (wm) { who = wm[1]; base = base.slice(0, wm.index).replace(/[\s,|\-]+$/, ''); }

    // b) colour (and graphic / design name, which counts as part of the colourway)
    var color = '';
    var design = '';
    var model = base;
    var parts = base.split(' - ').map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length >= 2) {
      // feature segments ("MIPS") belong to the model wherever they sit:
      // "M10 Helmet - Flood - MIPS - Blue" and "M10 Helmet - MIPS - Era - Pink" -> "M10 Helmet - MIPS"
      var feats = [];
      var rest = [parts[0]];
      for (var pi = 1; pi < parts.length; pi++) {
        if (RE_FEATURE_SEG.test(parts[pi])) feats.push(parts[pi]);
        else rest.push(parts[pi]);
      }
      var last = rest.length > 1 ? rest[rest.length - 1] : '';
      var lastHasColor = !!last && last.split(/\s+/).some(isColorToken);
      if (last && (rest.length >= 3 || lastHasColor || (last.split(/\s+/).length <= 4 && !RE_SIZE_TAIL.test(' ' + last)))) {
        color = last;
        rest.pop();
        // "Supertech M10 Helmet - Flood - MIPS - Blue/Black Carbon": segments between the model
        // and a real colour are the graphic ("Flood"). Only when the colour has a colour word, so
        // spec segments ("Bib Mousse - Standard - 1.85in") never become a colourway.
        if (rest.length >= 2 && lastHasColor) {
          // words that change the product ("Carbon Brave": carbon shell) stay with the model
          var dk = [];
          var dg = [];
          rest.slice(1).join(' - ').split(/\s+/).forEach(function (t) {
            if (VARIANT_WORDS[t.toLowerCase().replace(/[^a-z0-9']/g, '')]) dk.push(t); else dg.push(t);
          });
          design = dg.join(' ').replace(/^-\s*|\s*-$/g, '');
          rest = [rest[0] + (dk.length ? ' ' + dk.join(' ') : '')];
        }
      }
      model = rest.concat(feats).join(' - ');
    } else {
      var toks = base.split(' ');
      var j = toks.length;
      while (j > 1 && isColorToken(toks[j - 1])) j--;
      // keep at least two words of model; a colour run of at most 6 words
      if (j < toks.length && j >= 1 && toks.length - j <= 6) {
        color = toks.slice(j).join(' ');
        model = toks.slice(0, j).join(' ');
      }
    }
    model = model.replace(/[\s,|\-]+$/, '').trim();
    if (who) model += ' ' + who;

    var display = stripVendor(model, vendor);
    // c) graphic inside the model name ("Thor Sportmode Anarchy Gloves", "Fly Kinetic Verdict Helmet")
    if (!design && display.indexOf(' - ') < 0) {
      var lg = lineGraphic(display, vendor);
      if (lg) { display = lg.model; design = lg.design; }
    }
    var label = design ? design + (color ? ' – ' + color : '') : color;
    var sz = normSize(size);
    var vkey = keyOf(vendorVariants(vendor)[vendorVariants(vendor).length - 1] || vendor || '');
    var mkey = keyOf(display);
    // the model key carries the vendor so two brands never merge
    return {
      model: display,
      modelKey: (keyOf(vendor) || vkey) + '|' + mkey,
      color: label,
      design: design,
      colorKey: colorKeyOf(design ? design + ' ' + color : color),
      size: sz.label,
      sizeKey: sz.key,
      sizeRank: sz.rank
    };
  }

  /* ---- 1b. Colour-only parser for parts (data-sm-group-mode="color") ----
     Parts and stickers (snippets/sm-group-gear.liquid outputs "color") only ever group by
     colour, never by size: "Acerbis Radiator Louvers - Black - CRF 250R/450R 2986000001" and
     "... - Red - CRF 250R/450R 2986000227" are one card with two swatches. Strict rule:
       - the trailing part number is dropped;
       - the colour is the LAST " - " segment made only of colour words (at most 5 words,
         joined by "/", "&", "and" or spaces, at least one real colour: "Light" alone is not one),
         or, for titles without " - ", a trailing run of colour words (a leading "Light" /
         "Dark" / "Bright" stays with the name: "Tail Light Red" -> "Tail Light" + "Red");
       - everything else must be identical (vendor, bike fitment, size, mm, teeth, links,
         gallons ...), so the key is the vendor + the whole title with the colour taken out.
     A group is only used when every member has a different colour (colourGroupOk): two
     "Fuel Tank - 3.1 Gallon - Honda - Black" with different part numbers fit different
     bikes, so that title never groups at all. */
  var PC_WORDS = (
    'black white red blue navy grey gray green lime orange yellow fluo fluorescent neon hi-vis hivis hi vis ' +
    'pink purple violet teal aqua cyan tan khaki sand brown olive camo silver gold charcoal stealth matte gloss ' +
    'glossy titanium gunmetal graphite chrome bronze copper smoke clear dark light bright burgundy maroon magenta ' +
    'natural anodized polished raw'
  ).split(' ');
  var PC_SET = {};
  PC_WORDS.forEach(function (w) { PC_SET[w] = 1; });
  // words that only modify a colour ("Light Blue"); never a colour on their own
  var PC_MOD = { dark: 1, light: 1, bright: 1, hi: 1, vis: 1 };
  var RE_PC_SKU = /\s+[A-Z0-9]*\d[A-Z0-9\-.\/]{3,}\s*$/;

  function pcWords(s) {
    return String(s || '').toLowerCase().split(/[\s\/&]+/).filter(Boolean);
  }
  function isPcWord(w) { return PC_SET[w] === 1 || w === 'and'; }
  function isPcPhrase(s) {
    var w = pcWords(s);
    if (!w.length || w.length > 5) return false;
    var real = false;
    for (var i = 0; i < w.length; i++) {
      if (!isPcWord(w[i])) return false;
      if (w[i] !== 'and' && !PC_MOD[w[i]]) real = true;
    }
    return real && w[0] !== 'and' && w[w.length - 1] !== 'and';
  }
  // "Black/Red" == "Black & Red" == "black / red"; gray = grey
  function pcColorKey(c) {
    return pcWords(String(c || '').replace(/\band\b/gi, '/'))
      .map(function (w) { return COLOR_MAP[w] || w; })
      .join('/');
  }

  function parseColor(title, vendor) {
    var none = { model: '', modelKey: '', color: '', design: '', colorKey: '', size: '', sizeKey: '', sizeRank: 9999, mode: 'color' };
    var full = clean(title);
    var skuM = RE_PC_SKU.exec(full);
    var s = full.replace(RE_PC_SKU, '').replace(/[\s,|\-]+$/, '').trim();
    if (!s) return none;
    var color = '';
    var model = '';
    var keyText = '';
    var seg = s.split(/\s+-\s+/);
    if (seg.length > 1) {
      for (var i = seg.length - 1; i >= 1; i--) {
        if (isPcPhrase(seg[i])) {
          color = seg[i].trim();
          var rest = seg.slice(0, i).concat(seg.slice(i + 1));
          model = rest.join(' - ');
          seg[i] = '#';
          keyText = seg.join(' - ');
          break;
        }
      }
    } else {
      var w = s.split(/\s+/);
      var j = w.length;
      while (j > 0) {
        var parts = w[j - 1].toLowerCase().split('/');
        if (!parts.every(function (p) { return p && PC_SET[p] === 1; })) break;
        j--;
      }
      // a leading "Light" / "Dark" / "Bright" belongs to the name ("Tail Light Red")
      while (j < w.length && PC_MOD[w[j].toLowerCase()]) j++;
      // keep at least two words of name, at most 5 colour words
      if (j >= 2 && j < w.length && w.length - j <= 5 && isPcPhrase(w.slice(j).join(' '))) {
        color = w.slice(j).join(' ');
        model = w.slice(0, j).join(' ');
        keyText = model + ' #';
      }
    }
    if (!color) return none;
    model = model.replace(/[\s,|\-]+$/, '').trim();
    var vk = String(vendor || '').toLowerCase().replace(/\s+/g, ' ').trim();
    // the whole vendor name first ("IMS Products Inc. Gas Tank" -> "Gas Tank"), then the usual short forms
    var display = vk && model.toLowerCase().indexOf(vk + ' ') === 0 ? model.slice(vk.length).trim() : stripVendor(model, vendor);
    return {
      model: display,
      modelKey: 'c|' + vk + '|' + keyText.toLowerCase().replace(/\s+/g, ' ').trim(),
      color: color,
      design: '',
      colorKey: pcColorKey(color),
      size: '',
      sizeKey: '',
      sizeRank: 9999,
      sku: skuM ? skuM[0].trim().toUpperCase() : '',
      mode: 'color'
    };
  }

  // How alike two part numbers are: the same start + the same end, over the shorter one
  // ("2986000001" / "2986000227" = .7, "JTA897.50BLK" / "JTA897.50ORG" = .75,
  //  "JTA895.48BLK" / "JTA894.48ORG" = .42, "2284570001" / "2983190003" = .1)
  function skuLikeness(a, b) {
    var n = Math.min(a.length, b.length);
    if (!n) return 1;
    var p = 0;
    while (p < n && a.charAt(p) === b.charAt(p)) p++;
    var s = 0;
    while (s < n - p && a.charAt(a.length - 1 - s) === b.charAt(b.length - 1 - s)) s++;
    return (p + s) / n;
  }

  // A colour group is only valid when
  //  (a) no two members share a colour, and
  //  (b) the part numbers in the titles (when there are any) belong to one family: colours of one
  //      part differ only in the colour code ("2986000001" / "2986000227"); parts for other bikes
  //      have another number ("2284570001" / "2983190003") and stay separate cards.
  function colourGroupOk(members) {
    var seen = {};
    var skus = [];
    for (var i = 0; i < members.length; i++) {
      var k = members[i].colorKey || '-';
      if (seen[k]) return false;
      seen[k] = 1;
      if (members[i].sku) skus.push(members[i].sku);
    }
    for (var x = 0; x < skus.length; x++) {
      for (var y = x + 1; y < skus.length; y++) {
        if (skuLikeness(skus[x], skus[y]) < 0.6) return false;
      }
    }
    return true;
  }

  // parse() for either mode ("color" = parts, anything else = gear)
  function parseMode(title, vendor, mode) {
    return mode === 'color' ? parseColor(title, vendor) : parse(title, vendor);
  }

  // Search words for the sibling lookup (model words; short / joining words dropped)
  function searchTerms(model) {
    var words = String(model || '')
      .replace(/[®™©?()]/g, ' ')
      .replace(/\s-\s/g, ' ')
      .split(/\s+/)
      .filter(function (w) {
        return w.replace(/[^A-Za-z0-9]/g, '').length >= 2 && !/^(with|and|the|for)$/i.test(w) && !RE_FEATURE_SEG.test(w);
      });
    return words.slice(0, 7).join(' ');
  }

  var api = {
    __sm: true,
    parse: parse,
    parseColor: parseColor,
    parseMode: parseMode,
    colourGroupOk: colourGroupOk,
    normSize: normSize,
    keyOf: keyOf,
    colorKeyOf: colorKeyOf,
    searchTerms: searchTerms,
    _boot: function () {}
  };

  /* ------------------------------------------------------------------------
     2. Sibling data (pure helpers, also used by the Node tests)
     ------------------------------------------------------------------------ */
  function imgAt(url, w) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + w);
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }

  // Collection index row (templates/collection.sm-group-index.liquid):
  // [handle, title, vendor, price, compare, available, variant id, image, url?] -> member
  function rowToMember(r, root) {
    var g = parse(r[1], r[2]);
    var img = r[7] || '';
    return {
      handle: r[0], title: r[1], vendor: r[2], url: r[8] || ((root || '/') + 'products/' + r[0]),
      price: +r[3] || 0, compare: +r[4] || 0, available: r[5] === 1 || r[5] === true, id: String(r[6] || ''),
      img: img ? imgAt(img, 600) : '', thumb: img ? imgAt(img, 120) : '', gear: true, single: true,
      model: g.model, modelKey: g.modelKey, color: g.color, colorKey: g.colorKey, design: g.design,
      size: g.size, sizeKey: g.sizeKey, sizeRank: g.sizeRank
    };
  }

  // Smaller rows for storage: the shared CDN prefix once, no width= (asked again when used)
  function packRows(rows) {
    var prefix = null;
    var out = rows.map(function (r) {
      var c = r.slice();
      var i = String(c[7] || '').replace(/([?&])width=\d+&?/, '$1').replace(/[?&]$/, '');
      var m = /^(.*?\/cdn\/shop\/)(.+)$/.exec(i);
      if (m) {
        if (prefix === null) prefix = m[1];
        if (m[1] === prefix) i = m[2];
      }
      c[7] = i;
      return c;
    });
    return { p: prefix || '', r: out };
  }
  function unpackRow(r, prefix) {
    var i = r[7];
    if (!i || !prefix || /^(?:https?:)?\/\//.test(i)) return r;
    var c = r.slice();
    c[7] = prefix + i;
    return c;
  }

  // members -> { modelKey: [member] } (gear sold one product per size / colour only)
  function mapByModel(members) {
    var map = {};
    members.forEach(function (m) {
      if (!m || !m.gear || !m.single || !m.modelKey || /\|$/.test(m.modelKey)) return;
      (map[m.modelKey] || (map[m.modelKey] = [])).push(m);
    });
    return map;
  }

  // Add new members to a group / refresh ones that did not come from the page.
  // Members rendered on this page keep their own price and stock (the page is the freshest copy).
  function mergeMembers(members, list) {
    var changed = false;
    var at = {};
    members.forEach(function (m, i) { at[m.handle] = i; });
    (list || []).forEach(function (m) {
      if (!m || !m.handle) return;
      if (at[m.handle] === undefined) {
        var copy = {};
        for (var k in m) if (Object.prototype.hasOwnProperty.call(m, k)) copy[k] = m[k];
        copy.fromPage = false;
        at[m.handle] = members.length;
        members.push(copy);
        changed = true;
        return;
      }
      var cur = members[at[m.handle]];
      if (!cur.thumb && m.thumb) { cur.thumb = m.thumb; changed = true; }
      if (!cur.img && m.img) { cur.img = m.img; changed = true; }
      if (cur.fromPage === false && (cur.price !== m.price || cur.compare !== m.compare || cur.available !== m.available)) {
        cur.price = m.price; cur.compare = m.compare; cur.available = m.available;
        changed = true;
      }
    });
    return changed;
  }

  // Longest distinctive word of a model name: every title of that model contains it, so rows
  // without it are skipped before the (slower) title parsing.
  var GENERIC_WORDS = { glove: 1, gloves: 1, helmet: 1, helmets: 1, jersey: 1, jerseys: 1, pant: 1, pants: 1, youth: 1, women: 1, womens: 1, boots: 1, jacket: 1, mips: 1 };
  function modelWord(model) {
    return (String(model || '').toLowerCase().match(/[a-z0-9]{3,}/g) || [])
      .filter(function (w) { return !GENERIC_WORDS[w]; })
      .sort(function (a, b) { return b.length - a.length; })[0] || '';
  }

  // Index rows -> lookup(modelKey, model) -> [member]. Titles are parsed only when needed
  // (and once), so a page with 30 models does not parse 1,200 titles up front.
  function makeIndex(rows, t, root) {
    var lower = [];
    var memo = [];
    var cache = {};
    return {
      t: t,
      size: rows.length,
      lookup: function (modelKey, model) {
        if (cache[modelKey]) return cache[modelKey];
        var word = modelWord(model);
        var out = [];
        for (var i = 0; i < rows.length; i++) {
          if (word) {
            if (lower[i] === undefined) lower[i] = String(rows[i][1] || '').toLowerCase();
            if (lower[i].indexOf(word) < 0) continue;
          }
          var m = memo[i] || (memo[i] = rowToMember(rows[i], root));
          if (m.modelKey === modelKey) out.push(m);
        }
        cache[modelKey] = out;
        return out;
      }
    };
  }

  api.imgAt = imgAt;
  api.modelWord = modelWord;
  api.makeIndex = makeIndex;
  api.rowToMember = rowToMember;
  api.packRows = packRows;
  api.unpackRow = unpackRow;
  api.mapByModel = mapByModel;
  api.mergeMembers = mergeMembers;
  api.skuLikeness = skuLikeness;

  /* ------------------------------------------------------------------------
     3. Browser part (sibling loading, grid cards, product page)
     ------------------------------------------------------------------------ */
  if (typeof window === 'undefined' || typeof document === 'undefined') return api;

  // per-model search cache (search results page, product page, fallback)
  var TTL = 30 * 60 * 1000;
  var CACHE_PREFIX = 'smg2:';
  var inflight = {};
  var queryCache = {};
  // collection index cache
  var IDX_VER = 'smgi1';
  var IDX_TTL = 6 * 60 * 60 * 1000; // used for up to 6 hours
  var IDX_FRESH = 30 * 60 * 1000; // refreshed in the background after 30 minutes
  var IDX_MAX = 3000; // bigger collections keep the per-model search
  var IDX_CONC = 3;
  var idxMem = {};
  var idxLoading = {};
  var idxFailed = {};

  function rootUrl() {
    return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
  }

  function storage(kind) {
    try {
      var s = window[kind];
      s.setItem('__smg', '1');
      s.removeItem('__smg');
      return s;
    } catch (e) { return null; }
  }
  var LS = storage('localStorage');
  var SS = storage('sessionStorage');

  function cacheGet(key) {
    try {
      var raw = SS && SS.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || Date.now() - o.t > TTL) return null;
      return o.m;
    } catch (e) { return null; }
  }
  function cacheSet(key, members) {
    try { if (SS) SS.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), m: members })); } catch (e) {}
  }

  /* ---------- per-model search (templates/search.sm-siblings.liquid) ---------- */
  function fetchQuery(q, modelKey, maxPages) {
    var ck = q + '|' + modelKey + '|' + maxPages;
    if (queryCache[ck]) return queryCache[ck];
    var all = [];
    function page(n) {
      var url = rootUrl() + 'search?type=product&view=sm-siblings&q=' + encodeURIComponent(q) + (n > 1 ? '&page=' + n : '');
      var get = function () { return fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } }); };
      return get()
        // too many requests: one more try 2 s later
        .then(function (r) { return r.status === 429 ? new Promise(function (ok) { setTimeout(ok, 2000); }).then(get) : r; })
        .then(function (r) { if (!r.ok) throw new Error('siblings ' + r.status); return r.text(); })
        .then(function (txt) {
          var j = JSON.parse(txt);
          var items = j.items || [];
          all = all.concat(items);
          // results come by relevance: stop once the end of a page no longer matches the model
          var still = items.slice(-25).some(function (it) { return it && parseMode(it.t, it.v, it.k ? 'color' : 'gear').modelKey === modelKey; });
          if (j.pages > n && n < maxPages && still) return page(n + 1);
          return all;
        });
    }
    var p = page(1).catch(function () { delete queryCache[ck]; return null; });
    queryCache[ck] = p;
    return p;
  }

  function toMember(it) {
    var mode = it.k ? 'color' : 'gear';
    var g = parseMode(it.t, it.v, mode);
    return {
      handle: it.h, title: it.t, vendor: it.v, url: it.u,
      price: +it.p || 0, compare: +it.c || 0, available: !!it.a, id: String(it.id || ''),
      img: it.i || '', thumb: it.s || '', gear: (it.g !== 0 && it.g !== false) || !!it.k, single: it.d !== 0 && it.d !== false,
      model: g.model, modelKey: g.modelKey, color: g.color, colorKey: g.colorKey, design: g.design,
      size: g.size, sizeKey: g.sizeKey, sizeRank: g.sizeRank, mode: mode, sku: g.sku || ''
    };
  }

  /* Siblings of a model by search: Promise<[member]> (null when the lookup failed).
     opts.maxPages: result pages of 250 to read at most (default 4). */
  function siblings(model, vendor, modelKey, opts) {
    if (!modelKey) modelKey = parse(model, vendor).modelKey; // parts always pass their "c|" key
    var maxPages = (opts && opts.maxPages) || 4;
    var cached = cacheGet(modelKey);
    if (cached) return Promise.resolve(cached);
    if (inflight[modelKey]) return inflight[modelKey];
    var q = searchTerms(model);
    if (!q) return Promise.resolve(null);
    var p = fetchQuery(q, modelKey, maxPages).then(function (items) {
      delete inflight[modelKey];
      if (!items) return null;
      var seen = {};
      var out = [];
      items.forEach(function (it) {
        if (!it || seen[it.h]) return;
        seen[it.h] = 1;
        var m = toMember(it);
        if (m.modelKey === modelKey && m.gear && m.single) out.push(m);
      });
      cacheSet(modelKey, out);
      return out;
    });
    inflight[modelKey] = p;
    return p;
  }
  api.siblings = siblings;

  /* ---------- collection index (templates/collection.sm-group-index.liquid) ---------- */
  function idxKey(handle) { return IDX_VER + ':' + handle; }

  function buildIdx(rows, t) {
    return makeIndex(rows, t, rootUrl());
  }

  function readStored(handle) {
    var stores = [LS, SS];
    for (var i = 0; i < stores.length; i++) {
      var s = stores[i];
      if (!s) continue;
      try {
        var raw = s.getItem(idxKey(handle));
        if (!raw) continue;
        var o = JSON.parse(raw);
        if (!o || !o.r || Date.now() - o.t > IDX_TTL) { s.removeItem(idxKey(handle)); continue; }
        return o;
      } catch (e) {}
    }
    return null;
  }

  // Remove the oldest other collection index (or one from an older cache version).
  // false when there is nothing left to remove.
  function evictOne(s, keepHandle) {
    var oldest = null;
    var oldestT = Infinity;
    var keys = [];
    try { for (var i = 0; i < s.length; i++) keys.push(s.key(i)); } catch (e) { return false; }
    for (var j = 0; j < keys.length; j++) {
      var k = keys[j];
      if (!k || !/^smgi\d+:/.test(k) || k === idxKey(keepHandle)) continue;
      if (k.indexOf(IDX_VER + ':') !== 0) { try { s.removeItem(k); } catch (e) {} return true; }
      var t = 0;
      try { var m = /^\{"t":(\d+)/.exec(s.getItem(k) || ''); t = m ? +m[1] : 0; } catch (e) {}
      if (t < oldestT) { oldestT = t; oldest = k; }
    }
    if (oldest) { try { s.removeItem(oldest); } catch (e) {} return true; }
    return false;
  }

  function writeStored(handle, rows, t) {
    var packed = packRows(rows);
    var str;
    try { str = JSON.stringify({ t: t, p: packed.p, r: packed.r }); } catch (e) { return 'memory'; }
    var stores = [[LS, 'local'], [SS, 'session']];
    for (var i = 0; i < stores.length; i++) {
      var s = stores[i][0];
      if (!s) continue;
      for (var tries = 0; tries < 8; tries++) {
        try { s.setItem(idxKey(handle), str); return stores[i][1]; } catch (e) {
          if (!evictOne(s, handle)) break; // quota: drop older collection indexes and try again
        }
      }
      try { s.removeItem(idxKey(handle)); } catch (e) {}
    }
    return 'memory';
  }

  function fetchIndexPage(handle, n) {
    var url = rootUrl() + 'collections/' + encodeURIComponent(handle) + '?view=sm-group-index' + (n > 1 ? '&page=' + n : '');
    return fetch(url, { credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error('index ' + r.status); return r.text(); })
      .then(function (txt) { return JSON.parse(txt); });
  }

  // page 1 first (it tells how many pages), then the rest, 3 at a time
  function fetchIndex(handle) {
    function get(n) { return fetchIndexPage(handle, n).catch(function () { return fetchIndexPage(handle, n); }); }
    return get(1).then(function (first) {
      var pages = Math.min(first.pages || 1, Math.ceil(IDX_MAX / 250));
      var parts = [first.items || []];
      var next = 2;
      var active = 0;
      return new Promise(function (resolve, reject) {
        var failed = false;
        function launch() {
          if (failed) return;
          if (next > pages && active === 0) { resolve([].concat.apply([], parts)); return; }
          while (active < IDX_CONC && next <= pages) {
            (function (n) {
              active++;
              get(n).then(function (j) {
                parts[n - 1] = j.items || [];
                active--;
                launch();
              }, function (e) { failed = true; reject(e); });
            })(next++);
          }
        }
        launch();
      });
    });
  }

  // Index already on this device (memory or storage), or null. Never waits.
  function indexNow(handle) {
    var m = idxMem[handle];
    if (m && Date.now() - m.t <= IDX_TTL) return m;
    var o = readStored(handle);
    if (!o) return null;
    var idx = buildIdx(o.r.map(function (r) { return unpackRow(r, o.p); }), o.t);
    idxMem[handle] = idx;
    return idx;
  }

  // Promise<index | null>: fetches when missing or older than 30 minutes
  function loadIndex(handle, force) {
    var have = indexNow(handle);
    if (have && !force && Date.now() - have.t <= IDX_FRESH) return Promise.resolve(have);
    if (idxLoading[handle]) return idxLoading[handle];
    var p = fetchIndex(handle).then(function (rows) {
      var t = Date.now();
      writeStored(handle, rows, t);
      var idx = buildIdx(rows, t);
      idxMem[handle] = idx;
      delete idxLoading[handle];
      return idx;
    }, function () {
      delete idxLoading[handle];
      return null;
    });
    idxLoading[handle] = p;
    return p;
  }
  api.loadIndex = loadIndex;

  // Siblings from any collection index this device has (product page): [member] or null
  function siblingsFromIndexes(self) {
    var word = modelWord(self.model);
    var best = null;
    function consider(list) {
      if (list && list.length > 1 && (!best || list.length > best.length)) best = list;
    }
    Object.keys(idxMem).forEach(function (h) { consider(idxMem[h].lookup(self.modelKey, self.model)); });
    if (best) return best;
    var root = rootUrl();
    [LS, SS].forEach(function (s) {
      if (!s) return;
      var keys = [];
      try { for (var i = 0; i < s.length; i++) keys.push(s.key(i)); } catch (e) { return; }
      keys.forEach(function (k) {
        if (!k || k.indexOf(IDX_VER + ':') !== 0) return;
        try {
          var o = JSON.parse(s.getItem(k));
          if (!o || !o.r || Date.now() - o.t > IDX_TTL) return;
          var list = [];
          o.r.forEach(function (r) {
            if (word && String(r[1]).toLowerCase().indexOf(word) < 0) return; // cheap pre-check before parsing
            var m = rowToMember(unpackRow(r, o.p), root);
            if (m.modelKey === self.modelKey) list.push(m);
          });
          consider(list);
        } catch (e) {}
      });
    });
    return best;
  }

  /* ---------- helpers ---------- */
  // shop.money_format comes from data-sm-money on the grid / product page box ("${{amount}}")
  var moneyFmt = null;
  function money(cents) {
    if (moneyFmt === null) {
      var holder = document.querySelector('[data-sm-money]');
      moneyFmt = (holder && holder.getAttribute('data-sm-money')) || '${{amount}}';
    }
    var n2 = (cents / 100).toFixed(2);
    var withCommas = n2.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return moneyFmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount_no_decimals') return withCommas.replace(/\.\d+$/, '');
      if (k === 'amount_with_comma_separator') return n2.replace('.', ',');
      return withCommas;
    }).replace(/<[^>]*>/g, '');
  }
  function el(tag, cls, attrs) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (attrs) for (var k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) e.setAttribute(k, attrs[k]);
    return e;
  }
  // Swatch thumbnail: small, never competes with the product images. The URL waits in
  // data-smg-defer until the main images the shopper sees first are done (see releaseSwatches).
  // (Not data-smg-src: renderCard keeps the card's own server image in img.dataset.smgSrc,
  // and releaseSwatches must never touch those main images.)
  var swatchesGo = false;
  function swatchImg(src) {
    var im = el('img', '', {
      alt: '', width: '120', height: '120',
      loading: 'lazy', decoding: 'async', fetchpriority: 'low'
    });
    if (swatchesGo) im.src = imgAt(src, 120);
    else im.setAttribute('data-smg-defer', imgAt(src, 120));
    return im;
  }
  function releaseSwatches() {
    swatchesGo = true;
    Array.prototype.forEach.call(document.querySelectorAll('img[data-smg-defer]'), function (im) {
      im.src = im.getAttribute('data-smg-defer');
      im.removeAttribute('data-smg-defer');
    });
  }
  api._releaseSwatches = releaseSwatches;

  // Release swatch images when the important images are complete, or after a time limit,
  // whichever comes first (window 'load' is too late: third-party scripts delay it).
  //   grid: every main card image in the first visible row, or 2.5 s after DOMContentLoaded
  //   product page: the main product image, or 1.5 s
  function scheduleSwatches() {
    var done = false;
    function go() {
      if (done) return;
      done = true;
      var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 50); };
      idle(releaseSwatches, { timeout: 500 });
    }
    var pdpImg = document.querySelector('[data-sm-grp-pdp]') && document.querySelector('.simple-main-image img');
    var waitFor = [];
    var limit = 2500;
    if (pdpImg) {
      waitFor = [pdpImg];
      limit = 1500;
    } else {
      var imgs = Array.prototype.filter.call(document.querySelectorAll('[data-sm-group-grid] .product-img--primary'), function (im) {
        var r = im.getBoundingClientRect();
        return r.width > 0 && r.top < window.innerHeight && r.bottom > 0;
      });
      if (imgs.length) {
        var top = Math.round(imgs[0].getBoundingClientRect().top);
        waitFor = imgs.filter(function (im) { return Math.abs(Math.round(im.getBoundingClientRect().top) - top) < 8; });
      }
    }
    setTimeout(go, limit);
    var left = waitFor.filter(function (im) { return !im.complete; }).length;
    if (!waitFor.length || !left) { go(); return; }
    waitFor.forEach(function (im) {
      if (im.complete) return;
      var once = function () {
        im.removeEventListener('load', once);
        im.removeEventListener('error', once);
        if (--left <= 0) go();
      };
      im.addEventListener('load', once);
      im.addEventListener('error', once);
    });
  }
  var uid = 0;

  // Swatch without a distinct photo: a colour dot from the colour name ("Black/Fluro Red" = two
  // halves), or null when a part is not a known colour (the swatch then shows two letters).
  var DOT = {
    black: '#111111', white: '#ffffff', grey: '#8a8a8a', gray: '#8a8a8a', charcoal: '#3a3a3a', anthracite: '#2f3133',
    graphite: '#41444a', gunmetal: '#53565b', silver: '#c0c0c0', red: '#d93025', burgundy: '#7a1f2b', maroon: '#6d1a24',
    orange: '#f57c00', yellow: '#ffd700', gold: '#b8860b', green: '#2e7d32', olive: '#6b6b1f', lime: '#9acd32',
    blue: '#1e5fbf', navy: '#1a2744', teal: '#008080', cyan: '#00bcd4', turquoise: '#30c5c0', purple: '#6a3fa0',
    pink: '#e6789a', brown: '#6b4423', tan: '#d2b48c', beige: '#d8c8a8', sand: '#c8b28a', khaki: '#a39264',
    camo: '#5b5f3a', cream: '#f3ecd8', stone: '#a8a296', bronze: '#8c6239', copper: '#b06a3b',
    // parts finishes
    chrome: '#d9dde1', titanium: '#878681', smoke: '#4a4a4a', clear: '#eef3f6', aqua: '#00b3b3', violet: '#7f4fc9',
    magenta: '#c2185b', stealth: '#2b2b2b', natural: '#e9e4d4', raw: '#b5b5b0', polished: '#cfd3d6', anodized: '#7a8a99'
  };
  var DOT_FLUO = { red: '#ff3b30', yellow: '#e5ff00', green: '#39e639', orange: '#ff6a00', pink: '#ff4fa3', blue: '#00a2ff' };
  function dotColors(name) {
    var parts = String(name || '').toLowerCase().split(/\s*\/\s*|\s+&\s+|\s+and\s+/).filter(Boolean).slice(0, 2);
    if (!parts.length) return null;
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var words = parts[i].replace(/hi[\s\-]?vis/g, 'fluo').split(/[^a-z]+/).filter(Boolean);
      var fluo = words.some(function (w) { return /^(?:fluo|fluro|flo|neon|fluorescent)$/.test(w); });
      var hex = '';
      for (var j = words.length - 1; j >= 0 && !hex; j--) hex = (fluo && DOT_FLUO[words[j]]) || DOT[words[j]] || '';
      if (!hex) return null;
      out.push(hex);
    }
    return out;
  }
  function swatchFallback(b, name) {
    var cols = dotColors(name);
    if (cols) {
      var dot = el('span', 'sm-grp__sw-dot', { 'aria-hidden': 'true' });
      dot.style.background = cols.length > 1 ? 'linear-gradient(135deg, ' + cols[0] + ' 50%, ' + cols[1] + ' 50%)' : cols[0];
      b.appendChild(dot);
    } else {
      b.appendChild(el('span', 'sm-grp__sw-txt')).textContent = name.slice(0, 2);
    }
  }

  // Group members -> colours (ordered by first appearance) and sizes (ranked)
  function index(members, firstColor) {
    var colors = [];
    var cmap = {};
    var sizes = [];
    var smap = {};
    members.forEach(function (m) {
      var ck = m.colorKey || '-';
      if (!cmap[ck]) { cmap[ck] = { key: ck, label: m.color || '', members: [] }; colors.push(cmap[ck]); }
      cmap[ck].members.push(m);
      if (m.sizeKey && !smap[m.sizeKey]) { smap[m.sizeKey] = { key: m.sizeKey, label: m.size, rank: m.sizeRank }; sizes.push(smap[m.sizeKey]); }
    });
    sizes.sort(function (a, b) { return a.rank - b.rank; });
    colors.forEach(function (c) {
      c.available = c.members.some(function (m) { return m.available; });
      var bySize = c.members.slice().sort(function (a, b) { return a.sizeRank - b.sizeRank; });
      c.rep = bySize.filter(function (m) { return m.available && m.img; })[0] ||
        bySize.filter(function (m) { return m.img; })[0] || bySize[0];
    });
    // the card's own colour first, then available colours, keep first-seen order otherwise
    colors.sort(function (a, b) {
      if (firstColor) {
        if (a.key === firstColor) return -1;
        if (b.key === firstColor) return 1;
      }
      return (b.available ? 1 : 0) - (a.available ? 1 : 0);
    });
    return { colors: colors, cmap: cmap, sizes: sizes };
  }

  function find(members, colorKey, sizeKey) {
    for (var i = 0; i < members.length; i++) {
      var m = members[i];
      if ((m.colorKey || '-') === colorKey && (m.sizeKey || '') === (sizeKey || '')) return m;
    }
    return null;
  }

  // Roving tabindex radio group: arrows move focus (and select when `selectOnArrow`)
  function radioKeys(group, onPick, selectOnArrow) {
    group.addEventListener('keydown', function (e) {
      var keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
      if (keys.indexOf(e.key) < 0) return;
      var items = Array.prototype.filter.call(group.querySelectorAll('[role="radio"]'), function (b) { return !b.hidden; });
      var cur = items.indexOf(document.activeElement);
      if (cur < 0) return;
      e.preventDefault();
      var next = cur;
      if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = items.length - 1;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (cur + 1) % items.length;
      else next = (cur - 1 + items.length) % items.length;
      items.forEach(function (b, i) { b.tabIndex = i === next ? 0 : -1; });
      items[next].focus();
      if (selectOnArrow && items[next].getAttribute('aria-disabled') !== 'true') onPick(items[next]);
    });
  }

  function setRoving(group) {
    var items = group.querySelectorAll('[role="radio"]');
    var checked = null;
    Array.prototype.forEach.call(items, function (b) { if (b.getAttribute('aria-checked') === 'true' && !b.hidden) checked = b; });
    var first = checked || Array.prototype.filter.call(items, function (b) { return !b.hidden; })[0];
    Array.prototype.forEach.call(items, function (b) { b.tabIndex = b === first ? 0 : -1; });
  }

  /* ------------------------------------------------------------------------
     4. Grid cards
     ------------------------------------------------------------------------ */
  function memberFromCard(card) {
    var d = card.dataset;
    var mode = d.smGroupMode === 'color' ? 'color' : 'gear';
    var g = parseMode(d.smGTitle, d.smGVendor, mode);
    return {
      handle: d.smGHandle, title: d.smGTitle, vendor: d.smGVendor, url: d.smGUrl,
      price: +d.smGPrice || 0, compare: +d.smGCompare || 0, available: d.smGAvail === 'true', id: d.smGId || '',
      img: d.smGImg || '', thumb: d.smGThumb || '', gear: true, single: true,
      model: g.model, modelKey: g.modelKey, color: g.color, colorKey: g.colorKey, design: g.design,
      size: g.size, sizeKey: g.sizeKey, sizeRank: g.sizeRank, mode: mode, sku: g.sku || ''
    };
  }

  // search mode only: look up siblings when a card scrolls near the viewport
  var io = null;
  function observe(card) {
    if (!('IntersectionObserver' in window)) { loadSiblings(card); return; }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) { io.unobserve(en.target); loadSiblings(en.target); }
        });
      }, { rootMargin: '300px 0px' });
    }
    io.observe(card);
  }

  // A small queue so a page of cards does not fire 20 searches at once
  var queue = [];
  var running = 0;
  function enqueue(fn) {
    queue.push(fn);
    pump();
  }
  function pump() {
    while (running < 3 && queue.length) {
      running++;
      queue.shift()().then(done, done);
    }
    function done() { running--; pump(); }
  }

  function loadSiblings(card) {
    var st = card.__smg;
    if (!st || st.loaded) return;
    st.loaded = true;
    enqueue(function () {
      return siblings(st.model, st.vendor, st.modelKey).then(function (list) {
        if (!list || !card.isConnected) return;
        if (mergeMembers(st.members, list) || !st.built) renderCard(card);
      });
    });
  }

  /* ---------- parts (colour-only mode): groups that break the rules ----------
     Parts only group with colour siblings on the same page (no collection index, no searches).
     Parts whose title is shared by two products of the same colour (different bikes), or
     whose part numbers are from different families (colourGroupOk), never group. When that
     shows only at a later card of the page, the cards folded so far go back into the grid
     right after the first one, and that card is a normal card again. */
  var badKeys = {};
  function colourCheck(card) {
    var st = card.__smg;
    if (!st || st.mode !== 'color' || st.broken || colourGroupOk(st.members)) return false;
    breakGroup(card);
    return true;
  }
  function breakGroup(card) {
    var st = card.__smg;
    badKeys[st.modelKey] = 1;
    st.broken = true;
    var grid = card.closest('[data-sm-group-grid]');
    if (grid && grid.__smgKeys && grid.__smgKeys[st.modelKey] === card) delete grid.__smgKeys[st.modelKey];
    // folded cards back in, in their order, after this one
    var li = card.closest('.grid__item') || card;
    var after = li;
    (st.folded || []).forEach(function (f) {
      if (f.isConnected || !after.parentNode) return;
      after.parentNode.insertBefore(f, after.nextSibling);
      after = f;
    });
    st.folded = [];
    // this card shows its own product again
    st.members = [st.lead];
    st.sel = { color: st.lead.colorKey || '-', size: '' };
    st.idx = index(st.members, st.sel.color);
    if (st.touched) applySelection(card, false);
    var ui = card.querySelector('[data-smg-ui]');
    if (ui) { ui.innerHTML = ''; ui.hidden = true; }
    card.classList.remove('sm-grp-card');
    var ta = card.querySelector('.product-title a');
    if (ta && ta.hasAttribute('data-smg-title')) {
      ta.textContent = ta.getAttribute('data-smg-title');
      ta.removeAttribute('data-smg-title');
      ta.removeAttribute('aria-label');
    }
    Array.prototype.forEach.call(card.querySelectorAll('.product-sku, .sm-card__fit'), function (x) { x.hidden = false; });
    if (grid) applyLimit(grid);
  }

  function gridHandle(grid) {
    var h = grid.getAttribute('data-smg-collection');
    var total = parseInt(grid.getAttribute('data-smg-total'), 10) || 0;
    if (!h || idxFailed[h] || total > IDX_MAX || !window.fetch || !window.Promise) return '';
    return h;
  }

  function leads(grid) {
    var byKey = grid.__smgKeys || {};
    return Object.keys(byKey).map(function (k) { return byKey[k]; }).filter(function (c) { return c.isConnected && c.__smg; });
  }

  // fill every card of the grid from the collection index
  function upgradeGrid(grid, idx) {
    if (!grid.isConnected) return;
    leads(grid).forEach(function (card) {
      var st = card.__smg;
      if (st.idxT === idx.t || st.mode === 'color') return; // parts: on-page siblings only
      st.idxT = idx.t;
      if (mergeMembers(st.members, idx.lookup(st.modelKey, st.model)) || !st.built) renderCard(card);
    });
  }

  function groupGrid(grid) {
    var cards = grid.querySelectorAll('.product-card-square[data-sm-gear="true"]:not([data-smg-done])');
    if (!cards.length) { applyLimit(grid); return; }
    var byKey = grid.__smgKeys || (grid.__smgKeys = {});
    Array.prototype.forEach.call(cards, function (card) {
      card.setAttribute('data-smg-done', '1');
      var m = memberFromCard(card);
      if (!m.modelKey || /\|$/.test(m.modelKey)) return;
      if (badKeys[m.modelKey]) return; // parts that must not group stay plain cards
      var lead = byKey[m.modelKey];
      if (lead && lead.isConnected && lead !== card) {
        // later card of a model already shown: fold it into the first card
        var st = lead.__smg;
        if (st.mode === 'color') {
          if (st.members.some(function (x) { return x.handle === m.handle; })) return;
          if (!colourGroupOk(st.members.concat([m]))) { breakGroup(lead); return; }
        }
        if (!st.members.some(function (x) { return x.handle === m.handle; })) st.members.push(m);
        var li = card.closest('.grid__item') || card;
        if (li.parentNode) {
          if (st.mode === 'color') st.folded.push(li);
          li.parentNode.removeChild(li);
        }
        st.dirty = true;
        return;
      }
      byKey[m.modelKey] = card;
      card.__smg = { model: m.model, vendor: m.vendor, modelKey: m.modelKey, members: [m], lead: m, dirty: true, mode: m.mode, folded: [] };
    });

    var handle = gridHandle(grid);
    var idx = handle ? indexNow(handle) : null;
    leads(grid).forEach(function (c) {
      var st = c.__smg;
      if (!st.dirty) return;
      st.dirty = false;
      // parts (colour mode): only the colour siblings on this page; no index, no searches
      if (st.mode === 'color') {
        if (!colourCheck(c)) renderCard(c);
        return;
      }
      // cards render at once from what the page has (plus the index when this device has it)
      if (idx) { mergeMembers(st.members, idx.lookup(st.modelKey, st.model)); st.idxT = idx.t; }
      renderCard(c);
      if (!handle) observe(c);
    });
    applyLimit(grid);

    // the collection index is only for gear cards (a parts-only grid never asks for it)
    var gearLeads = leads(grid).some(function (c) { return c.__smg.mode !== 'color'; });
    if (handle && gearLeads && (!idx || Date.now() - idx.t > IDX_FRESH)) {
      loadIndex(handle, !!idx).then(function (fresh) {
        if (fresh) { upgradeGrid(grid, fresh); return; }
        if (idx) return; // keep the older copy
        // no index: fall back to one search per model as cards come into view
        idxFailed[handle] = true;
        leads(grid).forEach(function (c) { if (c.__smg.mode !== 'color') observe(c); });
      });
    }
  }

  /* ---------- "Show more styles" ---------- */
  function gridItems(grid) {
    return Array.prototype.filter.call(grid.children, function (li) { return li.classList && li.classList.contains('grid__item'); });
  }

  function applyLimit(grid) {
    var limit = parseInt(grid.getAttribute('data-smg-limit'), 10) || 0;
    var items = gridItems(grid);
    var total = items.length;
    var shown = limit ? Math.min(total, Math.max(limit, grid.__smgShown || 0)) : total;
    items.forEach(function (li, i) { li.classList.toggle('smg-later', i >= shown); });
    grid.__smgStyles = total;
    grid.__smgVisible = shown;
    var box = grid.__smgMore;
    if (limit && total > limit) {
      if (!box || !box.isConnected) box = buildMore(grid);
      var left = total - shown;
      box.querySelector('.sm-grp-more__status').textContent = 'Showing ' + shown + ' of ' + total + ' styles';
      var btn = box.querySelector('.sm-grp-more__btn');
      btn.hidden = left <= 0;
      btn.textContent = 'Show more styles (' + left + ')';
      box.hidden = false;
    } else if (box) {
      box.hidden = true;
    }
    updateCount(grid);
  }

  function buildMore(grid) {
    var box = el('div', 'sm-grp-more');
    var status = el('p', 'sm-grp-more__status', { role: 'status' });
    var btn = el('button', 'sm-grp-more__btn', { type: 'button' });
    box.appendChild(status);
    box.appendChild(btn);
    grid.parentNode.insertBefore(box, grid.nextSibling);
    grid.__smgMore = box;
    btn.addEventListener('click', function () {
      var limit = parseInt(grid.getAttribute('data-smg-limit'), 10) || 30;
      var before = grid.__smgVisible || 0;
      grid.__smgShown = before + limit;
      applyLimit(grid);
      var items = gridItems(grid);
      // cards that were hidden had no width: lay their swatch row out again
      items.slice(before, grid.__smgVisible).forEach(function (li) {
        var c = li.querySelector('.product-card-square');
        if (c && c.__smg && c.__smg.built) renderCard(c);
      });
      // move focus to the first card that just appeared
      var first = items[before];
      var target = first && (first.querySelector('.product-title a') || first.querySelector('a[href], button:not([disabled])'));
      if (target) target.focus();
    });
    return box;
  }

  function updateCount(grid) {
    var total = grid.__smgStyles || 0;
    var folded = grid.querySelectorAll('.sm-grp-card').length;
    var limited = (parseInt(grid.getAttribute('data-smg-limit'), 10) || 0) > 0;
    var pages = parseInt(grid.getAttribute('data-smg-pages'), 10) || 1;
    ['ProductCountDesktop', 'ProductCount'].forEach(function (id) {
      var span = document.getElementById(id);
      if (!span || !span.parentNode) return;
      var out = span.parentNode.querySelector('.sm-grp-count');
      if (!total || (!folded && !limited)) { if (out) out.hidden = true; return; }
      if (!out) {
        out = el('span', 'sm-grp-count');
        span.parentNode.insertBefore(out, span.nextSibling);
      }
      out.hidden = false;
      // Sisters Moto: "50 shown on this page" reads better than the internal word "styles"
      var text = ' · ' + total + ' shown';
      var extra = pages > 1 ? ' on this page' : '';
      if (out.getAttribute('data-text') !== text + extra) {
        out.setAttribute('data-text', text + extra);
        out.textContent = text;
        if (extra) out.appendChild(el('span', 'sm-grp-count__page')).textContent = extra;
      }
    });
  }

  // Photo swatches only when every colour has its own photo (else colour dots)
  function distinctPhotos(colors) {
    var seen = {};
    for (var i = 0; i < colors.length; i++) {
      var r = colors[i].rep;
      var u = r && (r.img || r.thumb);
      if (!u) return false;
      u = u.replace(/[?&]width=\d+/, '');
      if (seen[u]) return false;
      seen[u] = 1;
    }
    return true;
  }

  function renderCard(card) {
    var st = card.__smg;
    if (!st) return;
    var idx = index(st.members, st.lead.colorKey || '-');
    var ui = card.querySelector('[data-smg-ui]');
    if (!ui) return;
    var multi = st.members.length > 1;
    if (!st.sel) {
      // start on the colour the server rendered in this card (its image is already loading), with no
      // size picked: a pre-picked size went into the cart for shoppers who never looked at the size row.
      // One-size colours still pick themselves (below and in applySelection).
      st.sel = { color: st.lead.colorKey || '-', size: '' };
    }
    if (!idx.cmap[st.sel.color]) st.sel.color = idx.colors[0].key;
    var colorSizes = idx.cmap[st.sel.color].members.filter(function (m) { return m.sizeKey; });
    // The first render only knows the products on this page, so a model can look one-size until the
    // full list arrives: an automatic one-size pick is undone then, unless the shopper has picked.
    if (st.autoSize && !st.touched) { st.sel.size = ''; st.autoSize = false; }
    if (idx.sizes.length === 1 && colorSizes.length === 1) {
      if (st.sel.size !== idx.sizes[0].key) st.autoSize = true;
      st.sel.size = idx.sizes[0].key;
    }
    st.idx = idx;
    st.built = true;

    if (!multi && !st.lead.size && !st.lead.color) {
      ui.hidden = true;
      return;
    }
    // parts: the colour row only for a real group (two colours or more), never a size row
    if (st.mode === 'color') {
      if (!multi || st.broken || !colourGroupOk(st.members)) { ui.hidden = true; return; }
      st.noSwatchImg = !distinctPhotos(idx.colors);
    }
    ui.hidden = false;
    // keep keyboard focus if the shopper is in the rows while the full list arrives
    var focusKey = null;
    var focusRow = null;
    if (ui.contains(document.activeElement) && document.activeElement.getAttribute('data-key') !== null) {
      focusKey = document.activeElement.getAttribute('data-key');
      focusRow = document.activeElement.classList.contains('sm-grp__size') ? '.sm-grp__size' : '.sm-grp__sw';
    }
    ui.innerHTML = '';
    card.classList.add('sm-grp-card');
    var id = 'smg' + (++uid);

    // colours
    if (idx.colors.length > 1 || (idx.colors[0] && idx.colors[0].label)) {
      var row = el('div', 'sm-grp__row sm-grp__row--colors');
      var lab = el('p', 'sm-grp__label', { id: id + 'c' });
      lab.appendChild(document.createTextNode('Colour: '));
      var cur = el('span', 'sm-grp__current');
      lab.appendChild(cur);
      row.appendChild(lab);
      var cg = el('div', 'sm-grp__swatches', { role: 'radiogroup', 'aria-labelledby': id + 'c' });
      // as many swatches as fit on one line (no jump when the full list arrives), "+N" for the rest
      var w = ui.clientWidth;
      var per = window.matchMedia && window.matchMedia('(max-width: 749px)').matches ? 33 : 35;
      var MAX = st.allColors ? Infinity : (w ? (idx.colors.length * per <= w ? idx.colors.length : Math.max(2, Math.floor((w - 34) / per))) : 6);
      idx.colors.forEach(function (c, i) {
        var b = el('button', 'sm-grp__sw', { type: 'button', role: 'radio', 'data-key': c.key });
        var name = c.label || 'Standard';
        b.setAttribute('aria-label', 'Colour: ' + name + (c.available ? '' : ' – sold out'));
        b.title = name;
        if (!c.available) b.classList.add('is-soldout');
        var src = !st.noSwatchImg && c.rep && (c.rep.thumb || c.rep.img);
        if (src) b.appendChild(swatchImg(src));
        else swatchFallback(b, name);
        if (i >= MAX) b.hidden = true;
        cg.appendChild(b);
      });
      row.appendChild(cg);
      if (idx.colors.length > MAX) {
        var more = el('button', 'sm-grp__more', { type: 'button', 'aria-label': 'Show ' + (idx.colors.length - MAX) + ' more colours' });
        more.textContent = '+' + (idx.colors.length - MAX);
        more.addEventListener('click', function () {
          st.allColors = true;
          Array.prototype.forEach.call(cg.querySelectorAll('[role="radio"][hidden]'), function (x) { x.hidden = false; });
          more.parentNode.removeChild(more);
          setRoving(cg);
          var first = cg.querySelectorAll('[role="radio"]')[MAX];
          if (first) first.focus();
        });
        row.appendChild(more);
      }
      ui.appendChild(row);
      cg.addEventListener('click', function (e) {
        var b = e.target.closest('[role="radio"]');
        if (b) pickColor(card, b.getAttribute('data-key'));
      });
      radioKeys(cg, function (b) { pickColor(card, b.getAttribute('data-key')); }, true);
    }

    // sizes
    if (idx.sizes.length) {
      var srow = el('div', 'sm-grp__row sm-grp__row--sizes');
      var slab = el('p', 'sm-grp__label visually-hidden', { id: id + 's' });
      slab.textContent = st.sizePrefix ? 'Size (' + st.sizePrefix + ')' : 'Size';
      srow.appendChild(slab);
      var sg = el('div', 'sm-grp__sizes', { role: 'radiogroup', 'aria-labelledby': id + 's' });
      idx.sizes.forEach(function (s) {
        var b = el('button', 'sm-grp__size', { type: 'button', role: 'radio', 'data-key': s.key });
        b.textContent = s.label;
        sg.appendChild(b);
      });
      srow.appendChild(sg);
      ui.appendChild(srow);
      sg.addEventListener('click', function (e) {
        var b = e.target.closest('[role="radio"]');
        if (!b) return;
        if (b.getAttribute('aria-disabled') === 'true') return;
        pickSize(card, b.getAttribute('data-key'));
      });
      radioKeys(sg, function (b) { pickSize(card, b.getAttribute('data-key')); }, false);
      sg.addEventListener('keydown', function (e) {
        if (e.key !== ' ' && e.key !== 'Enter') return;
        var b = e.target.closest('[role="radio"]');
        if (b && b.getAttribute('aria-disabled') === 'true') e.preventDefault();
      });
    }

    var live = el('p', 'visually-hidden sm-grp__live', { 'aria-live': 'polite' });
    ui.appendChild(live);

    // title -> model name when the card stands for several products (not for one product's variants)
    if (multi && !st.vmode) {
      // the SKU / fitment lines belong to one product only (parts: every colour has the same
      // fitment in its title, so the fitment line stays)
      Array.prototype.forEach.call(card.querySelectorAll(st.mode === 'color' ? '.product-sku' : '.product-sku, .sm-card__fit'), function (x) { x.hidden = true; });
      var ta = card.querySelector('.product-title a');
      if (ta && !ta.hasAttribute('data-smg-title')) {
        ta.setAttribute('data-smg-title', ta.textContent);
        var vendor = st.lead.vendor || '';
        ta.textContent = st.model;
        if (vendor && st.model.toLowerCase().indexOf(vendor.toLowerCase()) !== 0) {
          // vendor is shown above the title already; keep it for screen readers
          ta.setAttribute('aria-label', vendor + ' ' + st.model);
        }
      }
    }
    applySelection(card, false);
    if (focusKey !== null) {
      var back = ui.querySelector(focusRow + '[data-key="' + focusKey.replace(/"/g, '\\"') + '"]');
      if (back) back.focus();
    }
  }

  function pickColor(card, key) {
    var st = card.__smg;
    if (!st || st.sel.color === key) return;
    st.sel.color = key;
    st.touched = true;
    var c = st.idx.cmap[key];
    // keep the size if this colour has it; one-size colours select themselves
    var withSize = c.members.filter(function (m) { return m.sizeKey; });
    if (st.sel.size && !find(c.members, key, st.sel.size)) st.sel.size = '';
    if (st.idx.sizes.length === 1 && withSize.length === 1) st.sel.size = withSize[0].sizeKey;
    applySelection(card, true);
  }

  function pickSize(card, key) {
    var st = card.__smg;
    if (!st) return;
    st.sel.size = key;
    st.touched = true;
    applySelection(card, true);
  }

  function ensureForm(card) {
    var form = card.querySelector('form[data-quick-add]');
    if (form) return form;
    var old = card.querySelector('.quick-add-btn--soldout, .quick-add-btn--options');
    var overlay = card.querySelector('.product-info-overlay');
    if (!overlay) return null;
    form = el('form', 'product-quick-add', { action: rootUrl() + 'cart/add', method: 'post', 'data-quick-add': '' });
    form.innerHTML = '<input type="hidden" name="id" value=""><input type="hidden" name="quantity" value="1">' +
      '<button type="submit" class="quick-add-btn"><span class="quick-add-btn__label">Add to cart</span>' +
      '<span class="quick-add-btn__done" aria-hidden="true">Added ✓</span></button>';
    if (old) old.parentNode.replaceChild(form, old);
    else overlay.appendChild(form);
    return form;
  }

  // Block the quick add while no size is picked (runs before assets/quick-add-square.js, which listens on document)
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || !form.matches || !form.matches('.sm-grp-card form[data-quick-add]')) return;
    var btn = form.querySelector('.quick-add-btn');
    if (btn && btn.getAttribute('aria-disabled') === 'true') {
      e.preventDefault();
      e.stopImmediatePropagation();
      var card = form.closest('.product-card-square');
      var sz = card && card.querySelector('.sm-grp__sizes');
      if (sz) {
        sz.classList.remove('is-nudge');
        void sz.offsetWidth;
        sz.classList.add('is-nudge');
        var t = sz.querySelector('[tabindex="0"]') || sz.querySelector('[role="radio"]');
        if (t) t.focus();
      }
    }
  }, true);

  function applySelection(card, announce) {
    var st = card.__smg;
    var idx = st.idx;
    var c = idx.cmap[st.sel.color];
    var colorMembers = c.members;
    var sel = st.sel.size ? find(st.members, st.sel.color, st.sel.size) : null;
    var needSize = idx.sizes.length > 0 && !sel && colorMembers.length > 1;
    if (!sel && colorMembers.length === 1) sel = colorMembers[0];
    var show = sel || c.rep;

    // swatches
    var cg = card.querySelector('.sm-grp__swatches');
    if (cg) {
      Array.prototype.forEach.call(cg.querySelectorAll('[role="radio"]'), function (b) {
        var on = b.getAttribute('data-key') === st.sel.color;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        if (on && b.hidden) b.hidden = false;
      });
      setRoving(cg);
      var cur = card.querySelector('.sm-grp__current');
      if (cur) cur.textContent = c.label || 'Standard';
    }
    // sizes: availability for the selected colour
    var sg = card.querySelector('.sm-grp__sizes');
    if (sg) {
      Array.prototype.forEach.call(sg.querySelectorAll('[role="radio"]'), function (b) {
        var key = b.getAttribute('data-key');
        var m = find(st.members, st.sel.color, key);
        var label = b.textContent;
        var ok = !!(m && m.available);
        b.setAttribute('aria-checked', key === st.sel.size ? 'true' : 'false');
        b.classList.toggle('is-unavailable', !ok);
        if (ok) b.removeAttribute('aria-disabled');
        else b.setAttribute('aria-disabled', 'true');
        b.setAttribute('aria-label', 'Size ' + (st.sizePrefix ? st.sizePrefix + ' ' : '') + sizeWord(label) + (m ? (m.available ? '' : ' – sold out') : ' – not made in this colour'));
      });
      setRoving(sg);
    }

    // Until the shopper picks a colour / size the card keeps exactly what the server rendered
    // (lead product: image, links, price, badge, wishlist, quick add); only the rows above change.
    // Real-variant cards (st.vmode) start with a variant picked, so price / links / Add to cart
    // follow it at once; their image still stays the server-rendered one until a pick.
    // A size still has to be picked: only the quick add changes ("Select a size", blocked by the
    // submit listener above).
    if (!st.touched && !st.vmode) {
      if (needSize) {
        ensureForm(card);
        setQuickAdd(card, null, true);
      }
      return;
    }
    ensureForm(card);

    // price
    var priceEl = card.querySelector('.product-price');
    if (priceEl) {
      var p = priceEl;
      var html;
      if (sel) {
        var sale = sel.compare > sel.price;
        p.classList.toggle('product-price--sale', sale);
        html = (sale ? '<span class="visually-hidden">Sale price</span>' : '') +
          '<span class="product-price-now">' + money(sel.price) + '</span>' +
          (sale ? '<span class="visually-hidden">Regular price</span><s class="product-price-was">' + money(sel.compare) + '</s>' +
            '<span class="product-price-save">Save ' + money(sel.compare - sel.price) + '</span>' : '');
      } else {
        var prices = colorMembers.map(function (m) { return m.price; });
        var cmps = colorMembers.map(function (m) { return m.compare; });
        var min = Math.min.apply(null, prices);
        var max = Math.max.apply(null, prices);
        var same = min === max && Math.min.apply(null, cmps) === Math.max.apply(null, cmps);
        var r = c.rep;
        if (same && r && r.compare > r.price) {
          p.classList.add('product-price--sale');
          html = '<span class="visually-hidden">Sale price</span><span class="product-price-now">' + money(r.price) + '</span>' +
            '<span class="visually-hidden">Regular price</span><s class="product-price-was">' + money(r.compare) + '</s>' +
            '<span class="product-price-save">Save ' + money(r.compare - r.price) + '</span>';
        } else {
          p.classList.remove('product-price--sale');
          html = '<span class="product-price-now">' + (min !== max ? 'From ' : '') + money(min) + '</span>';
        }
      }
      p.innerHTML = html;
    }

    // image + links
    var wrap = card.querySelector('.product-image-wrapper');
    if (wrap && show) {
      if (show.url) wrap.href = show.url;
      var img = wrap.querySelector('.product-img--primary');
      var alt = wrap.querySelector('.product-img--secondary');
      // remember the server-rendered image once (before the first swap, so it can come back)
      if (img && !img.dataset.smgSrc) {
        img.dataset.smgSrc = img.getAttribute('src') || '';
        img.dataset.smgSrcset = img.getAttribute('srcset') || '';
        img.dataset.smgAlt = img.getAttribute('alt') || '';
      }
      // grouped: another product's photo; real variants: the variant photo when it differs
      // from the one the card was rendered with (a colour without its own photo shows that one)
      var swapTo = '';
      var restore = false;
      if (st.vmode) {
        if (st.touched) {
          if (show.img && show.img !== st.baseImg) swapTo = show.img;
          else restore = true;
        }
      } else if (show.img) {
        if (show.handle !== st.lead.handle) swapTo = show.img;
        else restore = true;
      }
      if (img && swapTo) {
        img.srcset = imgAt(swapTo, 300) + ' 300w, ' + imgAt(swapTo, 400) + ' 400w, ' + imgAt(swapTo, 600) + ' 600w';
        img.src = imgAt(swapTo, 600);
        img.alt = show.title;
        if (alt) { alt.hidden = true; wrap.classList.remove('has-alt'); }
      } else if (img && restore && img.getAttribute('src') !== img.dataset.smgSrc) {
        if (img.dataset.smgSrcset) img.srcset = img.dataset.smgSrcset;
        else img.removeAttribute('srcset');
        img.src = img.dataset.smgSrc;
        img.alt = img.dataset.smgAlt;
        if (alt) { alt.hidden = false; wrap.classList.add('has-alt'); }
      }
      var badge = wrap.querySelector('.merch-product-badge');
      var target = sel || show;
      var groupAvail = st.members.some(function (m) { return m.available; });
      card.classList.toggle('is-soldout', !groupAvail);
      if (badge && !badge.classList.contains('merch-product-badge--reserve')) {
        if (sel && !sel.available) {
          badge.className = 'merch-product-badge merch-product-badge--soldout';
          badge.textContent = 'Sold out';
          badge.hidden = false;
        } else if (target && target.compare > target.price && target.price > 0) {
          var pct = Math.round((target.compare - target.price) * 100 / target.compare);
          badge.className = 'merch-product-badge';
          badge.innerHTML = '<span class="badge-pct">' + pct + '%</span> <span class="badge-lbl">OFF</span>';
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }
    }
    var ta = card.querySelector('.product-title a');
    if (ta && show && show.url) ta.href = show.url;

    // wishlist heart follows the selected product
    var heart = card.querySelector('.sm-wish');
    if (heart && show && !st.vmode) {
      heart.setAttribute('data-handle', show.handle);
      heart.setAttribute('aria-label', 'Save ' + (sel ? sel.title : show.title));
      var on = !!(window.SmWishlist && window.SmWishlist.has && window.SmWishlist.has(show.handle));
      heart.setAttribute('aria-pressed', on ? 'true' : 'false');
      heart.classList.toggle('is-saved', on);
    }

    // real variants: the SKU line follows the picked variant
    if (st.vmode) {
      var skuEl = card.querySelector('.product-sku');
      if (skuEl) {
        var sku = sel ? sel.sku : '';
        if (sku) skuEl.textContent = 'SKU: ' + sku;
        skuEl.hidden = !sku;
      }
    }

    setQuickAdd(card, sel, needSize);

    if (announce) {
      var live = card.querySelector('.sm-grp__live');
      if (live) {
        live.textContent = (c.label ? c.label : '') + (sel && sel.size ? ', size ' + sel.size : '') +
          (sel ? ', ' + money(sel.price) + (sel.available ? '' : ', sold out') : (needSize ? '. Choose a size' : ''));
      }
    }
  }

  // The card's quick add for the current pick: "Select a size" / "Add to cart" / "Sold out"
  function setQuickAdd(card, sel, needSize) {
    var form = card.querySelector('form[data-quick-add]');
    if (!form) return;
    var input = form.querySelector('input[name="id"]');
    var btn = form.querySelector('.quick-add-btn');
    var labelEl = form.querySelector('.quick-add-btn__label');
    var ready = false;
    var text;
    if (needSize) {
      text = 'Select a size';
    } else if (sel && sel.available) {
      ready = true;
      text = 'Add to cart';
    } else {
      text = 'Sold out';
    }
    if (input) input.value = ready ? sel.id : (sel ? sel.id : '');
    if (btn) {
      if (ready) btn.removeAttribute('aria-disabled');
      else btn.setAttribute('aria-disabled', 'true');
      btn.classList.toggle('is-waiting', !ready);
    }
    if (labelEl) {
      labelEl.textContent = text;
      if (ready) {
        var vh = el('span', 'visually-hidden');
        vh.textContent = ': ' + sel.title;
        labelEl.appendChild(vh);
      }
    }
  }

  var SIZE_WORDS = { XS: 'extra small', S: 'small', M: 'medium', L: 'large', XL: 'extra large', '2XL': '2X large', '3XL': '3X large',
    YS: 'youth small', YM: 'youth medium', YL: 'youth large', YXL: 'youth extra large', YXS: 'youth extra small', Y2XS: 'youth 2X small' };
  function sizeWord(label) { return SIZE_WORDS[label] || label; }

  function scan() {
    var grids = document.querySelectorAll('[data-sm-group-grid]');
    Array.prototype.forEach.call(grids, groupGrid);
    scanVariants();
  }

  /* ------------------------------------------------------------------------
     4b. Cards of products with real Colour / Size variants (any page)
         snippets/sm-card-core.liquid marks them data-sm-variants and puts the variants in
         <script type="application/json" data-smv-json>:
         { h: handle, t: title, u: url, img: card image, id: picked variant id,
           sv: [size values in option order],
           v: [[variant id, colour, size, price, compare, available 1/0, image key, sku]],
           i: { image key: image url } }
         Same controls, classes and behaviour as the grouped gear cards; each variant is a member.
     ------------------------------------------------------------------------ */
  var RE_SIZE_PREFIX = /^(US|EU|UK|AU|CA|JP)\s+/i;

  function variantState(card) {
    var tag = card.querySelector('script[data-smv-json]');
    if (!tag) return null;
    var d;
    try { d = JSON.parse(tag.textContent); } catch (e) { return null; }
    if (!d || !d.v || !d.v.length || !d.u) return null;
    var imgs = d.i || {};
    var order = {};
    (d.sv || []).forEach(function (s, i) { order[String(s).toLowerCase()] = i; });
    // "US 5" ... "US 14": the shared prefix goes into the (hidden) row label, buttons say "5" ... "14"
    var sizesAll = d.v.map(function (r) { return String(r[2] || ''); }).filter(Boolean);
    var pm = sizesAll.length ? RE_SIZE_PREFIX.exec(sizesAll[0]) : null;
    var pre = pm && sizesAll.every(function (s) { return s.indexOf(pm[0]) === 0 && s.length > pm[0].length; }) ? pm[0] : '';
    var members = d.v.map(function (r) {
      var color = String(r[1] || '');
      var size = String(r[2] || '');
      var img = r[6] !== '' && r[6] !== null && imgs[r[6]] ? imgs[r[6]] : '';
      var bits = [color, size].filter(Boolean).join(' / ');
      var sk = size.toLowerCase();
      return {
        handle: d.h, id: String(r[0]), vmode: true,
        title: d.t + (bits ? ' - ' + bits : ''), vendor: '',
        url: d.u + (d.u.indexOf('?') >= 0 ? '&' : '?') + 'variant=' + r[0],
        price: +r[3] || 0, compare: +r[4] || 0, available: r[5] === 1 || r[5] === true,
        img: img ? imgAt(img, 600) : '', thumb: img ? imgAt(img, 120) : '', sku: String(r[7] || ''),
        model: d.t, modelKey: 'v:' + d.h, color: color, colorKey: color.toLowerCase(), design: '',
        size: pre ? size.slice(pre.length) : size, sizeKey: sk,
        sizeRank: order[sk] !== undefined ? order[sk] : 9999
      };
    });
    var lead = null;
    for (var i = 0; i < members.length && !lead; i++) if (members[i].id === String(d.id)) lead = members[i];
    lead = lead || members.filter(function (m) { return m.available; })[0] || members[0];
    // photo swatches only when every colour has its own photo; otherwise colour dots
    var firstImg = {};
    members.forEach(function (m) {
      var k = m.colorKey || '-';
      if (!(k in firstImg) || (!firstImg[k] && m.img)) firstImg[k] = m.img;
    });
    var keys = Object.keys(firstImg);
    var seen = {};
    var distinct = keys.every(function (k) {
      var u = firstImg[k];
      if (!u || seen[u]) return false;
      seen[u] = 1;
      return true;
    });
    return {
      vmode: true, model: d.t, vendor: '', modelKey: 'v:' + d.h, members: members, lead: lead,
      noSwatchImg: !distinct, sizePrefix: pre.trim(), baseImg: d.img ? imgAt(d.img, 600) : ''
    };
  }

  function scanVariants() {
    var cards = document.querySelectorAll('.product-card-square[data-sm-variants]:not([data-smg-done])');
    Array.prototype.forEach.call(cards, function (card) {
      card.setAttribute('data-smg-done', '1');
      try {
        var st = variantState(card);
        if (!st) return;
        card.__smg = st;
        renderCard(card);
      } catch (e) {
        // leave the server-rendered card as it is
        if (window.console) console.warn('sm-group: variant card', e);
      }
    });
  }

  /* ------------------------------------------------------------------------
     5. Product page (snippets/sm-group-pdp.liquid)
     ------------------------------------------------------------------------ */
  function initPdp(box) {
    if (box.__smg) return;
    var d = box.dataset;
    var self = {
      handle: d.handle, title: d.title, vendor: d.vendor, url: d.url, price: +d.price || 0, compare: +d.compare || 0,
      available: d.available === 'true', id: d.id, img: d.img || '', thumb: d.thumb || '', gear: true, single: true
    };
    var mode = d.smGroupMode === 'color' ? 'color' : 'gear';
    var g = parseMode(self.title, self.vendor, mode);
    for (var k in g) if (Object.prototype.hasOwnProperty.call(g, k)) self[k] = g[k];
    self.mode = mode;
    box.__smg = self;
    // parts whose title names no colour: nothing to pick
    if (mode === 'color' && !g.modelKey) { renderPdp(box, self, [self]); return; }
    function show(list) {
      var members = [self];
      (list || []).forEach(function (m) { if (m.handle !== self.handle) members.push(m); });
      renderPdp(box, self, members);
    }
    // a collection index on this device (shopper came from a collection page) answers at once
    var known = null;
    if (mode !== 'color') { try { known = siblingsFromIndexes(self); } catch (e) {} }
    if (known) { show(known); return; }
    // otherwise one search (at most 2 result pages, stops when results stop matching)
    // (parts: one result page of 250 is plenty for the colours of one part)
    siblings(g.model, self.vendor, g.modelKey, { maxPages: mode === 'color' ? 1 : 2 }).then(show, function () { show([]); });
  }

  function navTo(m) {
    if (!m || !m.url) return;
    var q = new URLSearchParams(window.location.search);
    q.delete('variant');
    var s = q.toString();
    window.location.href = m.url + (s ? (m.url.indexOf('?') >= 0 ? '&' : '?') + s : '');
  }

  function renderPdp(box, self, members) {
    var idx = index(members, self.colorKey || '-');
    var colorsEl = box.querySelector('[data-smg-colors]');
    var sizesEl = box.querySelector('[data-smg-sizes]');
    var showColors = idx.colors.length > 1;
    var showSizes = idx.sizes.length > 1 || (idx.sizes.length === 1 && members.length > 1);
    // parts: colour only, and only for a valid colour group (rules in colourGroupOk)
    if (self.mode === 'color') {
      showSizes = false;
      if (!colourGroupOk(members)) showColors = false;
    }
    if (!showColors && !showSizes) {
      box.hidden = true;
      box.classList.remove('is-loading');
      return;
    }
    if (self.mode === 'color') box.hidden = false; // parts: the box starts hidden (no space kept)
    var selColor = self.colorKey || '-';

    if (colorsEl) {
      var row = colorsEl.closest('.sm-grp-pdp__row');
      if (!showColors) { if (row) row.hidden = true; }
      else {
        colorsEl.innerHTML = '';
        var cur = box.querySelector('[data-smg-color-name]');
        if (cur) cur.textContent = self.color || 'Standard';
        // parts: colour dots when the colours have no photos of their own
        var noImg = self.mode === 'color' && !distinctPhotos(idx.colors);
        idx.colors.forEach(function (c) {
          var b = el('button', 'sm-grp-pdp__sw', { type: 'button', role: 'radio', 'data-key': c.key });
          var name = c.label || 'Standard';
          b.setAttribute('aria-checked', c.key === selColor ? 'true' : 'false');
          b.setAttribute('aria-label', 'Colour: ' + name + (c.available ? '' : ' – sold out'));
          b.title = name;
          if (!c.available) b.classList.add('is-soldout');
          var src = noImg ? '' : (c.key === selColor ? (self.thumb || self.img) : (c.rep && (c.rep.thumb || c.rep.img)));
          if (src) b.appendChild(swatchImg(src));
          else swatchFallback(b, name);
          colorsEl.appendChild(b);
        });
        setRoving(colorsEl);
        var goColor = function (b) {
          var key = b.getAttribute('data-key');
          if (key === selColor) return;
          var c = idx.cmap[key];
          var target = find(members, key, self.sizeKey);
          if (!target || !target.available) target = c.members.filter(function (m) { return m.available; })[0] || target || c.members[0];
          navTo(target);
        };
        colorsEl.addEventListener('click', function (e) { var b = e.target.closest('[role="radio"]'); if (b) goColor(b); });
        radioKeys(colorsEl, function () {}, false);
      }
    }

    if (sizesEl) {
      var srow = sizesEl.closest('.sm-grp-pdp__row');
      if (!showSizes) { if (srow) srow.hidden = true; }
      else {
        sizesEl.innerHTML = '';
        idx.sizes.forEach(function (s) {
          var m = find(members, selColor, s.key);
          var b = el('button', 'sm-grp-pdp__size', { type: 'button', role: 'radio', 'data-key': s.key });
          b.textContent = s.label;
          var on = s.key === self.sizeKey;
          b.setAttribute('aria-checked', on ? 'true' : 'false');
          var ok = !!(m && (m.available || on));
          if (!ok) { b.setAttribute('aria-disabled', 'true'); b.classList.add('is-unavailable'); }
          if (m && !m.available) b.classList.add('is-unavailable');
          b.setAttribute('aria-label', 'Size ' + sizeWord(s.label) + (m ? (m.available ? '' : ' – sold out') : ' – not made in this colour'));
          sizesEl.appendChild(b);
        });
        setRoving(sizesEl);
        var cur2 = box.querySelector('[data-smg-size-name]');
        if (cur2) cur2.textContent = self.size || '';
        sizesEl.addEventListener('click', function (e) {
          var b = e.target.closest('[role="radio"]');
          if (!b || b.getAttribute('aria-disabled') === 'true' || b.getAttribute('aria-checked') === 'true') return;
          navTo(find(members, selColor, b.getAttribute('data-key')));
        });
        radioKeys(sizesEl, function () {}, false);
      }
    }
    box.classList.remove('is-loading');
  }

  /* ------------------------------------------------------------------------
     6. Boot
     ------------------------------------------------------------------------ */
  api._boot = function () {
    var timer = null;
    function run() {
      timer = null;
      scan();
      Array.prototype.forEach.call(document.querySelectorAll('[data-sm-grp-pdp]'), initPdp);
    }
    function later() { if (!timer) timer = setTimeout(run, 30); }
    // assets/sm-group.css keeps grouped grids hidden until this first pass (no layout shift / CLS)
    function first() {
      try { run(); } finally { document.documentElement.classList.add('smg-ok'); }
      scheduleSwatches();
    }
    if (document.readyState === 'loading') {
      // Collection / search pages load this file right after the product grid (no defer):
      // group that grid now, before it is painted; the rest of the page at DOMContentLoaded.
      if (document.querySelector('[data-sm-group-grid]')) {
        first();
        document.addEventListener('DOMContentLoaded', run);
      } else {
        document.addEventListener('DOMContentLoaded', first);
      }
    } else first();
    // Filters / sorting / pagination re-render the grid (assets/facets.js replaces #ProductGridContainer)
    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (recs) {
        for (var i = 0; i < recs.length; i++) {
          var added = recs[i].addedNodes;
          for (var j = 0; j < added.length; j++) {
            var n = added[j];
            if (n.nodeType === 1 && (n.matches('[data-sm-group-grid], .grid__item, .product-card-square') ||
              (n.querySelector && n.querySelector('.product-card-square[data-sm-gear="true"], .product-card-square[data-sm-variants]')))) { later(); return; }
          }
        }
      });
      var start = function () { mo.observe(document.body, { childList: true, subtree: true }); };
      if (document.body) start(); else document.addEventListener('DOMContentLoaded', start);
    }
    window.addEventListener('pageshow', function (e) { if (e.persisted) later(); });
  };
  api.scan = scan;

  return api;
});
